import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PostaGoogleRiga } from './PostaGoogle';
import { api } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { Chip } from '../../components/ui/Chip';
import { Mascotte } from '../../components/ui/Mascotte';
import { Icon } from '../../components/ui/Icon';
import { consumiQuery, dollari, nomeModello, salvaImpostazioni, statoAssistenteQuery, type StatoAssistente } from '../assistente/assistenteApi';
import { chiamate, dollariCorti, meseDiAdesso, meseInParole, percentoSpeso } from '../assistente/registroApi';
import { AzioniAssistente } from '../assistente/AzioniAssistente';

/**
 * L'assistente in Impostazioni › Servizi collegati (28/10/2026): acceso o spento, quanto
 * ha speso oggi e nel mese, e la prova del collegamento. Lo paga il piano Max (200 $ di
 * crediti al mese, condivisi con Aura OS); il tetto è di 1 $ al giorno (Lorenzo, 28/10).
 *
 * Dalla sera del 28/10 (os-planning/ai/fase-1-progetto.md, decisione 15): gli interruttori
 * «MUNE acceso», «Solo lettura» e «Memoria di notte» (quest'ultimo arriva più
 * avanti); «Spesa di MUNE a ottobre» con una barra sola, speso su budget, e sotto
 * le righe per funzione e per persona (GET /ai/consumi); in fondo «Cosa ha fatto
 * MUNE», il registro con l'Annulla (AzioniAssistente). Solo per gli Amministratori:
 * la guardia sta in SettingsPage, perché tutte le rotte /ai rispondono 403 agli altri.
 *
 * Dal 29/10 (fetta 2A) c'era anche «Prova lo streaming», per vedere se la risposta arrivava a
 * pezzi dietro nginx. Tolto il 30/10: lo dice già la chat, e a Lorenzo non diceva niente
 * («ma che vuol dire prova lo streaming»). «Prova il collegamento» da allora si chiama
 * «Controlla MUNE». GET /ai/prova-flusso resta sull'API, per le prove da riga di comando.
 */
const STATO: Record<StatoAssistente['stato'], { etichetta: string; tono: 'ok' | 'warn' | 'ghost' | 'alert' }> = {
  attiva: { etichetta: 'Acceso', tono: 'ok' },
  finta: { etichetta: 'In prova: risposte finte', tono: 'warn' },
  spenta: { etichetta: 'Spento', tono: 'ghost' },
  sospesa: { etichetta: 'Fermo', tono: 'alert' },
  'senza-chiave': { etichetta: 'Manca la chiave', tono: 'ghost' },
};

export function AssistenteCard() {
  const qc = useQueryClient();
  const { data: s } = useQuery(statoAssistenteQuery);
  const { data: consumi } = useQuery(consumiQuery());
  // Senza stato nuovo c'è stata una chiamata (prova, riaccendi): si rifà anche la spesa del mese.
  const aggiorna = (nuovo?: StatoAssistente) => {
    if (nuovo) return qc.setQueryData(statoAssistenteQuery.queryKey, nuovo);
    qc.invalidateQueries({ queryKey: statoAssistenteQuery.queryKey });
    qc.invalidateQueries({ queryKey: ['ai', 'consumi'] });
  };

  const prova = useMutation({
    mutationFn: () => api.post<{ testo: string; finta: boolean }>('/ai/prova'),
    onSuccess: (r) => { aggiorna(); toast.ok(r.finta ? 'Collegamento di prova riuscito' : 'MUNE risponde', r.testo); },
    onError: (e: any) => { aggiorna(); toast.alert('MUNE non risponde', e?.message ?? 'Riprova'); },
  });

  const riaccendi = useMutation({
    mutationFn: () => api.post<{ testo: string }>('/ai/riaccendi'),
    onSuccess: () => { aggiorna(); toast.ok('MUNE è di nuovo acceso'); },
    onError: (e: any) => { aggiorna(); toast.alert('MUNE è ancora fermo', e?.message ?? 'Riprova'); },
  });

  // Gli interruttori: una chiamata sola, con il campo che cambia. La risposta è lo stato
  // nuovo, che va dritto nella cache (lo leggono il pannello e i tasti nelle task).
  const interruttore = useMutation({
    mutationFn: (body: { acceso?: boolean; soloLettura?: boolean }) => salvaImpostazioni(body),
    onSuccess: (nuovo, body) => {
      aggiorna(nuovo);
      if (body.acceso !== undefined) toast.ok(nuovo.impostazioni.acceso ? 'MUNE acceso' : 'MUNE spento');
      else toast.ok(nuovo.impostazioni.soloLettura ? 'Solo lettura accesa' : 'Solo lettura spenta');
    },
    onError: (e: any) => toast.alert('Impostazione di MUNE non salvata', e?.message ?? 'Riprova'),
  });

  const stato = s ? STATO[s.stato] : null;
  // La spesa del mese: dalla rotta dei consumi (ha anche le righe); finché non arriva, dallo stato.
  const mese = consumi?.mese ?? meseDiAdesso();
  const spesi = consumi?.spesiUsd ?? s?.spesiUsd ?? 0;
  const budget = consumi?.budgetUsd ?? s?.budgetUsd ?? 0;
  const percento = percentoSpeso(spesi, budget);

  return (
    <div className="card">
      <div className="card-hd flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Mascotte size={40} stato={s?.stato === 'sospesa' ? 'errore' : prova.isPending || riaccendi.isPending ? 'pensa' : s?.stato === 'spenta' ? 'dorme' : 'pronto'} />
          <div className="flex items-center gap-2">
            <div className="t-h3">MUNE</div>
            {stato && <Chip tone={stato.tono} dot>{stato.etichetta}</Chip>}
          </div>
        </div>
        {s && (
          <div className="flex gap-2 d-only">
            {s.stato === 'sospesa' && (
              <button className="btn btn-sm btn-primary" onClick={() => riaccendi.mutate()} disabled={riaccendi.isPending}>
                <Icon name="refresh" size={10} /> {riaccendi.isPending ? 'Provo…' : 'Riaccendi MUNE'}
              </button>
            )}
            {(s.stato === 'attiva' || s.stato === 'finta') && (
              <>
                {/* Solo computer, come tutta la fila (d-only). */}
                <button className="btn btn-sm" onClick={() => prova.mutate()} disabled={prova.isPending}>
                  <Icon name="message" size={10} /> {prova.isPending ? 'Controllo…' : 'Controlla MUNE'}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      <div className="card-bd text-[13px] mute">
        {!s ? null : s.stato === 'senza-chiave' ? (
          <div>
            Manca la chiave di Claude sul server: si mette nell'area di lavoro «Masamune OS» della console di Anthropic.
            Lo paga il piano Max, senza carta.
          </div>
        ) : (
          <>
            {s.stato === 'sospesa' && (
              <div className="mb-3 text-ink">
                Si è fermato da solo{s.impostazioni.motivoSospensione ? `: ${s.impostazioni.motivoSospensione}` : ''}. «Riaccendi MUNE» prova il collegamento e, se risponde, lo rimette in moto.
              </div>
            )}
            <div className="grid grid-cols-3 gap-4">
              <div>
                {/* Quello della tua chat (30/10/2026): si cambia col tasto accanto al nome, nel pannello di MUNE. */}
                <div className="t-micro">Modello</div>
                <div className="text-ink" title="Si cambia col tasto accanto al nome, nel pannello di MUNE">{nomeModello(s.modelloChat ?? s.modello)}</div>
              </div>
              <div>
                <div className="t-micro">Spesa di oggi</div>
                <div className="text-ink font-semibold">{dollari(s.spesiOggiUsd)} su {dollari(s.budgetGiornoUsd)}</div>
              </div>
              <div>
                <div className="t-micro">Chi paga</div>
                <div className="text-ink">Crediti del piano Max</div>
              </div>
            </div>
            <div className="mt-4">
              <Interruttore
                nome="MUNE acceso"
                spiega="Spento, il gestionale è quello di sempre: niente chat e niente tasti nelle task."
                valore={s.impostazioni.acceso}
                inCorso={interruttore.isPending}
                onCambia={(acceso) => interruttore.mutate({ acceso })}
              />
              <Interruttore
                nome="Solo lettura"
                spiega="Nella chat legge e risponde, ma non cambia niente. Dividi e Riscrivi restano: a salvare è sempre la persona."
                valore={s.impostazioni.soloLettura}
                inCorso={interruttore.isPending}
                onCambia={(soloLettura) => interruttore.mutate({ soloLettura })}
              />
              <Interruttore
                nome="Memoria di notte"
                spiega="Di notte aggiorna da sola le schede dei clienti. Arriva più avanti."
                valore={s.impostazioni.memoriaDiNotte}
                spento
              />
              {/* 2H (29/10/2026): la casella di chi è entrato, collegata per MUNE. */}
              <PostaGoogleRiga />
            </div>
          </>
        )}
      </div>

      {s && (
        <div className="card-bd border-t border-line text-[13px]">
          <div className="flex items-baseline justify-between gap-4">
            <div className="text-ink font-semibold">
              Spesa di MUNE a {meseInParole(mese)}: {dollari(spesi)} di {dollariCorti(budget)}
            </div>
            <div className="mute">restano {dollari(Math.max(0, budget - spesi))}</div>
          </div>
          {/* Una barra sola: lo speso pieno, il resto vuoto. Dall'80 % in rosso, come l'avviso agli Amministratori. */}
          <div className="barbar mt-2" role="img" aria-label={`Speso il ${Math.round(percento)} per cento del budget del mese`}>
            <span style={{ width: `${percento}%`, ...(percento >= 80 ? { background: 'var(--alert)' } : {}) }} />
          </div>
          {consumi && (consumi.perFunzione.length > 0 || consumi.perPersona.length > 0) ? (
            <div className="grid grid-cols-2 gap-6 mt-3">
              <div>
                <div className="t-micro mb-1">Per funzione</div>
                {consumi.perFunzione.map((f) => (
                  <div key={f.codice} className="row between py-0.5">
                    <span className="mute">{f.funzione}</span>
                    <span className="text-ink">{dollari(f.usd)} <span className="mute">· {chiamate(f.chiamate)}</span></span>
                  </div>
                ))}
              </div>
              <div>
                <div className="t-micro mb-1">Per persona</div>
                {consumi.perPersona.map((p) => (
                  <div key={p.id ?? p.nome} className="row between py-0.5">
                    <span className="mute">{p.nome}</span>
                    <span className="text-ink">{dollari(p.usd)} <span className="mute">· {chiamate(p.chiamate)}</span></span>
                  </div>
                ))}
              </div>
            </div>
          ) : consumi ? (
            <div className="mute mt-2">A {meseInParole(mese)} ancora nessuna spesa.</div>
          ) : null}
        </div>
      )}

      <div className="card-bd border-t border-line text-[13px]">
        <AzioniAssistente />
      </div>
    </div>
  );
}

/**
 * Un interruttore: il nome, cosa fa, e la pastiglia No / Sì (seg-control, «come un
 * interruttore vero»). `spento` lo mostra senza farlo toccare (Memoria di notte).
 */
function Interruttore({ nome, spiega, valore, onCambia, inCorso, spento }: {
  nome: string;
  spiega: string;
  valore: boolean;
  onCambia?: (v: boolean) => void;
  inCorso?: boolean;
  spento?: boolean;
}) {
  const bloccato = spento || inCorso || !onCambia;
  return (
    <div className="adm-line">
      <div className="min-w-0 flex-1">
        <div className="text-ink font-medium">{nome}</div>
        <div className="mute text-[12.5px]">{spiega}</div>
      </div>
      <div className={`seg-control tk-seg-sm shrink-0${spento ? ' opacity-50' : ''}`} role="group" aria-label={nome}>
        <button type="button" aria-pressed={!valore} disabled={bloccato} onClick={() => { if (valore) onCambia?.(false); }}>No</button>
        <button type="button" aria-pressed={valore} disabled={bloccato} onClick={() => { if (!valore) onCambia?.(true); }}>Sì</button>
      </div>
    </div>
  );
}
